import * as Redux from 'redux';
import { Dispatch } from 'react-redux';
import { ReportsState, ReportAdministrationView } from './ReportsState';
import { Query, Report, QueryCondition } from '../misc/api/apiTypes';
import * as AdministrationActions from './Administration/AdministrationActions';
import { MainState } from "../Core/reducers";
import { WebPortalLegacyClientInternal } from '../misc/api/WebPortalTokenClient';

declare const rootUrl: string;

export enum ActionType {
    InitializeReports = 'InitializeReports',
    InitializeNameFilter = 'InitializeNameFilter',
    FilterReports = 'FilterReports',
    ClearAllFilters = 'ClearAllFilters',
    SortReports = 'SortReports',
    ToggleGetReport = 'ToggleGetReport',
    GetReportFailed = 'GetReportFailed',
    PageReports = 'PageReports',
    RequireStudySelection = 'RequireStudySelection',
    HideWidgets = 'HideWidgets',
    OtherAction = '_OtherAction_',
    ToggleReportModal = 'ToggleReportModal'
}

export class InitializeReportsAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return async (dispatch: Dispatch<MainState>, getState: () => MainState) => {
            await this.testReportServerConnection(legacyClient, dispatch);

            const state = getState();

            await this.fetchReports(legacyClient, dispatch, state.reports.listTable.reportsQuery);
        }
    }

    static async testReportServerConnection(legacyClient: WebPortalLegacyClientInternal, dispatch: Dispatch<MainState>) {
        const success = await legacyClient.testStoredConnection();
        if (success) {
            dispatch(AdministrationActions.ChangeViewAction.create(ReportAdministrationView.Hidden));
        } else {
            dispatch(AdministrationActions.FailedToConnectToReportServerAction.create('Failed to connect to the Report Server. Please check your credentials'));
            dispatch(AdministrationActions.ChangeViewAction.create(ReportAdministrationView.Credentials));
        }
    }

    static fetchReports(legacyClient: WebPortalLegacyClientInternal, dispatch: Dispatch<MainState>, listTableQuery: Query<Report>) {
        // HACK: still using the older API here for now
        return legacyClient.getDefinedReports(listTableQuery)
            .then(query => {
                if (query) {
                    dispatch(InitializeReportsAction.create(query));
                } else {
                    dispatch(RequireStudySelectionAction.create());
                }
            });
    }
}

export class InitializeReportsAction implements Redux.Action {
    type: ActionType.InitializeReports;
    query: Query<Report>;

    static create(query: Query<Report>): InitializeReportsAction {
        return {
            type: ActionType.InitializeReports,
            query: query,
        }
    }
}

export class RequireStudySelectionAction implements Redux.Action {
    type: ActionType.RequireStudySelection;

    static create(): RequireStudySelectionAction {
        return {
            type: ActionType.RequireStudySelection,
        }
    }
}

export class InitializeNameFilterAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return (dispatch: Dispatch<MainState>, getState: () => MainState) => {
            legacyClient.getDefinedReports(getState().reports.listTable.nameFilterQuery)
                .then(json => dispatch(InitializeNameFilterAction.create(json)));
        }
    }
}

export class InitializeNameFilterAction implements Redux.Action {
    type: ActionType.InitializeNameFilter;
    query: Query<Report>;

    static create(query: Query<Report>): InitializeNameFilterAction {
        return {
            type: ActionType.InitializeNameFilter,
            query: query,
        }
    }
}

export class FilterReportsAction implements Redux.Action {
    type: ActionType.FilterReports;
    member: string;
    condition: QueryCondition;
    value: any;

    static create(member: keyof(Report), condition: QueryCondition, value: any): FilterReportsAction {
        return {
            type: ActionType.FilterReports,
            member: member,
            condition: condition,
            value: value,
        }
    }
}

export class ClearAllFiltersAction implements Redux.Action {
    type: ActionType.ClearAllFilters;

    static create(): ClearAllFiltersAction {
        return {
            type: ActionType.ClearAllFilters,
        }
    }
}

export class SortReportsAction implements Redux.Action {
    type: ActionType.SortReports;
    member: keyof(Report);

    static create(member: keyof(Report)): SortReportsAction {
        return {
            type: ActionType.SortReports,
            member: member,
        }
    }
}

export enum ReportFormat {
    PDF = 1,
    Excel = 2,
}

export class ToggleGetReportAction implements Redux.Action {
    type: ActionType.ToggleGetReport;
    reportId: string;
    isRequestingDownload: boolean;

    static create(reportId: string, isRequestingDownload: boolean): ToggleGetReportAction {
        return {
            type: ActionType.ToggleGetReport,
            reportId: reportId,
            isRequestingDownload: isRequestingDownload,
        }
    }
}

export class GetReportFailedAction implements Redux.Action {
    type: ActionType.GetReportFailed;
    reportId: string;

    static create(reportId: string): GetReportFailedAction {
        return {
            type: ActionType.GetReportFailed,
            reportId: reportId,
        }
    }
}

export class PageReportsAction implements Redux.Action {
    type: ActionType.PageReports;
    page: number;

    static create(page: number): PageReportsAction {
        return {
            type: ActionType.PageReports,
            page: page,
        }
    }
}

export class HideWidgetsAction implements Redux.Action {
    type: ActionType.HideWidgets;

    static create(): HideWidgetsAction {
        return {
            type: ActionType.HideWidgets,
        }
    }
}

export class OtherAction implements Redux.Action {
    type: ActionType.OtherAction;

    static create(): OtherAction {
        return {
            type: ActionType.OtherAction,
        }
    }
}

export class ToggleReportModalAction implements Redux.Action {
    type: ActionType.ToggleReportModal;
    id?: string;

    static create(id?: string): ToggleReportModalAction {
        return {
            type: ActionType.ToggleReportModal,
            id
        }
    }
}

export type Action = InitializeReportsAction | InitializeNameFilterAction | FilterReportsAction | ClearAllFiltersAction | SortReportsAction | ToggleGetReportAction | GetReportFailedAction | PageReportsAction | RequireStudySelectionAction | HideWidgetsAction | OtherAction | ToggleReportModalAction;
